import { Link } from "react-router-dom";
import { MapPin, Clock, CreditCard, MessageCircle, Flower2, Truck, Globe, Sun, Ship, Heart } from "lucide-react";

import { seoContent } from "@/data/citySeoContent";

const ITALY_CITIES = ["bari", "capri", "sorrento", "amalfi", "lake-como", "porto-cervo", "portofino", "florence", "verona", "bologna", "genoa", "porto-rotondo", "naples", "palermo", "catania", "venice"];


const iconMap = {
  truck: Truck,
  clock: Clock,
  card: CreditCard,
  chat: MessageCircle,
  flower: Flower2,
  photo: MapPin,
  globe: Globe,
  sun: Sun,
  ship: Ship,
  heart: Heart,
};

export default function SeoTextSection({ citySlug, lang }) {
  const cityData = seoContent[citySlug];
  if (!cityData) return null;

  const content = cityData[lang] || cityData.ru;

  return (
    <section className="bg-gray-50 border-t border-gray-100" data-testid="seo-text-section">
      <div className="max-w-5xl mx-auto px-4 py-16 sm:py-20">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
          {content.heading}
        </h2>

        <p className="text-gray-600 leading-relaxed mb-10 text-base">
          {content.intro}
        </p>

        <div className="grid gap-8 md:grid-cols-2 mb-12">
          {content.blocks.map((block, i) => (
            <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {block.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {block.text}
              </p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-12">
          {content.features.map((feature, i) => {
            const Icon = iconMap[feature.icon] || MapPin;
            return (
              <div
                key={i}
                className="flex items-start gap-3 bg-white rounded-lg p-4 border border-gray-100"
              >
                <Icon className="w-5 h-5 text-pink-500 mt-0.5 shrink-0" />
                <span className="text-sm text-gray-700">{feature.text}</span>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-100 mb-8">
          <p className="text-gray-600 text-sm leading-relaxed">
            {content.bottomText}
          </p>
        </div>

        {(() => {
          const CLUSTERS = {
            riviera: { title: { ru: "Доставка цветов рядом — по всему Лазурному берегу", en: "Flower delivery nearby — across the French Riviera" }, cities: ["nice", "cannes", "monaco", "saint-tropez"] },
            caucasus: { title: { ru: "Доставка цветов рядом — по всему Кавказу", en: "Flower delivery nearby — across the Caucasus" }, cities: ["tbilisi", "batumi", "yerevan", "baku"] },
            greek: { title: { ru: "Доставка цветов рядом — на греческих островах", en: "Flower delivery nearby — across the Greek islands" }, cities: ["mykonos", "santorini", "rhodes", "crete"] },
            turkey: { title: { ru: "Доставка цветов рядом — на турецком побережье", en: "Flower delivery nearby — across the Turkish coast" }, cities: ["antalya", "alanya", "bodrum", "marmaris", "istanbul", "mersin", "fethiye", "cesme"] },
            spain: { title: { ru: "Доставка цветов рядом — по всей Испании", en: "Flower delivery nearby — across Spain" }, cities: ["barcelona", "alicante", "ibiza", "malaga", "valencia", "costa-brava", "tenerife", "mallorca", "san-sebastian", "marbella", "gran-canaria"] },
            adriatic: { title: { ru: "Доставка цветов рядом — на Адриатике", en: "Flower delivery nearby — across the Adriatic" }, cities: ["dubrovnik", "hvar", "kotor", "budva"] },
            italy: { title: { ru: "Доставка цветов рядом — по Италии", en: "Flower delivery nearby — across Italy" }, cities: ITALY_CITIES },
            vietnam: { title: { ru: "Доставка цветов рядом — по Вьетнаму", en: "Flower delivery nearby — across Vietnam" }, cities: ["danang", "nhatrang", "phu-quoc"] },
            portugal: { title: { ru: "Доставка цветов рядом — по Португалии", en: "Flower delivery nearby — across Portugal" }, cities: ["lisbon", "algarve"] },
            egypt: { title: { ru: "Доставка цветов рядом — зимнее солнце: Египет, Эмираты, Кипр", en: "Flower delivery nearby — winter sun: Egypt, UAE, Cyprus" }, cities: ["sharm-el-sheikh", "hurghada", "el-gouna", "dubai", "abu-dhabi", "sharjah", "limassol", "antalya", "tenerife"] },
            switzerland: { title: { ru: "Доставка цветов рядом — Альпы и север Италии", en: "Flower delivery nearby — the Alps and northern Italy" }, cities: ["st-moritz", "zermatt", "verbier", "lake-como", "verona", "venice", "monaco", "nice", "cannes"] },
            thailand: { title: { ru: "Доставка цветов рядом — по Таиланду", en: "Flower delivery nearby — across Thailand" }, cities: ["phuket", "pattaya"] },
            asia: { title: { ru: "Доставка цветов рядом — в Азии", en: "Flower delivery nearby — across Asia" }, cities: ["phuket", "danang"] },
          };
          const LABELS = {
            nice: { ru: "Доставка цветов в Ницце", en: "Flower delivery in Nice" },
            cannes: { ru: "Доставка цветов в Каннах", en: "Flower delivery in Cannes" },
            monaco: { ru: "Доставка цветов в Монако", en: "Flower delivery in Monaco" },
            "saint-tropez": { ru: "Доставка цветов в Сен-Тропе", en: "Flower delivery in Saint-Tropez" },
            tbilisi: { ru: "Доставка цветов в Тбилиси", en: "Flower delivery in Tbilisi" },
            batumi: { ru: "Доставка цветов в Батуми", en: "Flower delivery in Batumi" },
            yerevan: { ru: "Доставка цветов в Ереване", en: "Flower delivery in Yerevan" },
            baku: { ru: "Доставка цветов в Баку", en: "Flower delivery in Baku" },
            mykonos: { ru: "Доставка цветов на Миконосе", en: "Flower delivery in Mykonos" },
            phuket: { ru: "Доставка цветов на Пхукете", en: "Flower delivery in Phuket" },
            pattaya: { ru: "Доставка цветов в Паттайе", en: "Flower delivery in Pattaya" },
            danang: { ru: "Доставка цветов в Дананге", en: "Flower delivery in Da Nang" },
            nhatrang: { ru: "Доставка цветов в Нячанге", en: "Flower delivery in Nha Trang" },
            "phu-quoc": { ru: "Доставка цветов на Фукуоке", en: "Flower delivery on Phu Quoc" },
            santorini: { ru: "Доставка цветов на Санторини", en: "Flower delivery in Santorini" },
            rhodes: { ru: "Доставка цветов на Родосе", en: "Flower delivery in Rhodes" },
            crete: { ru: "Доставка цветов на Крите", en: "Flower delivery in Crete" },
            antalya: { ru: "Доставка цветов в Анталье", en: "Flower delivery in Antalya" },
            alanya: { ru: "Доставка цветов в Алании", en: "Flower delivery in Alanya" },
            bodrum: { ru: "Доставка цветов в Бодруме", en: "Flower delivery in Bodrum" },
            marmaris: { ru: "Доставка цветов в Мармарисе", en: "Flower delivery in Marmaris" },
            istanbul: { ru: "Доставка цветов в Стамбуле", en: "Flower delivery in Istanbul" },
            mersin: { ru: "Доставка цветов в Мерсине", en: "Flower delivery in Mersin" },
            fethiye: { ru: "Доставка цветов в Фетхие", en: "Flower delivery in Fethiye" },
            barcelona: { ru: "Доставка цветов в Барселоне", en: "Flower delivery in Barcelona" },
            alicante: { ru: "Доставка цветов в Аликанте", en: "Flower delivery in Alicante" },
            ibiza: { ru: "Доставка цветов на Ибице", en: "Flower delivery in Ibiza" },
            malaga: { ru: "Доставка цветов в Малаге", en: "Flower delivery in Malaga" },
            valencia: { ru: "Доставка цветов в Валенсии", en: "Flower delivery in Valencia" },
            "costa-brava": { ru: "Доставка цветов на Коста-Брава", en: "Flower delivery on Costa Brava" },
            tenerife: { ru: "Доставка цветов на Тенерифе", en: "Flower delivery in Tenerife" },
            mallorca: { ru: "Доставка цветов на Майорке", en: "Flower delivery in Mallorca" },
            "san-sebastian": { ru: "Доставка цветов в Сан-Себастьяне", en: "Flower delivery in San Sebastián" },
            marbella: { ru: "Доставка цветов в Марбелье", en: "Flower delivery in Marbella" },
            "gran-canaria": { ru: "Доставка цветов на Гран-Канарии", en: "Flower delivery in Gran Canaria" },
            madrid: { ru: "Доставка цветов в Мадриде", en: "Flower delivery in Madrid" },
            seville: { ru: "Доставка цветов в Севилье", en: "Flower delivery in Seville" },
            porto: { ru: "Доставка цветов в Порту", en: "Flower delivery in Porto" },
            lagos: { ru: "Доставка цветов в Лагуше", en: "Flower delivery in Lagos" },
            casablanca: { ru: "Доставка цветов в Касабланке", en: "Flower delivery in Casablanca" },
            paris: { ru: "Доставка цветов в Париже", en: "Flower delivery in Paris" },
            marrakech: { ru: "Доставка цветов в Марракеше", en: "Flower delivery in Marrakech" },
            london: { ru: "Доставка цветов в Лондоне", en: "Flower delivery in London" },
            rome: { ru: "Доставка цветов в Риме", en: "Flower delivery in Rome" },
            vienna: { ru: "Доставка цветов в Вене", en: "Flower delivery in Vienna" },
            amsterdam: { ru: "Доставка цветов в Амстердаме", en: "Flower delivery in Amsterdam" },
            prague: { ru: "Доставка цветов в Праге", en: "Flower delivery in Prague" },
            berlin: { ru: "Доставка цветов в Берлине", en: "Flower delivery in Berlin" },
            zanzibar: { ru: "Доставка цветов на Занзибаре", en: "Flower delivery in Zanzibar" },
            haiphong: { ru: "Доставка цветов в Хайфоне", en: "Flower delivery in Haiphong" },
            dubrovnik: { ru: "Доставка цветов в Дубровнике", en: "Flower delivery in Dubrovnik" },
            hvar: { ru: "Доставка цветов на Хваре", en: "Flower delivery in Hvar" },
            kotor: { ru: "Доставка цветов в Которе", en: "Flower delivery in Kotor" },
            budva: { ru: "Доставка цветов в Будве", en: "Flower delivery in Budva" },
            bari: { ru: "Доставка цветов в Бари", en: "Flower delivery in Bari" },
            capri: { ru: "Доставка цветов на Капри", en: "Flower delivery in Capri" },
            "porto-cervo": { ru: "Доставка цветов в Порто-Черво", en: "Flower delivery in Porto Cervo" },
            "portofino": { ru: "Доставка цветов в Портофино", en: "Flower delivery in Portofino" },
            "florence": { ru: "Доставка цветов во Флоренции", en: "Flower delivery in Florence" },
            sorrento: { ru: "Доставка цветов в Сорренто", en: "Flower delivery in Sorrento" },
            amalfi: { ru: "Доставка цветов на Амальфи", en: "Flower delivery on the Amalfi Coast" },
            "lake-como": { ru: "Доставка цветов на озере Комо", en: "Flower delivery on Lake Como" },
            palermo: { ru: "Доставка цветов в Палермо", en: "Flower delivery in Palermo" },
            catania: { ru: "Доставка цветов в Катании", en: "Flower delivery in Catania" },
            venice: { ru: "Доставка цветов в Венеции", en: "Flower delivery in Venice" },
            "sharm-el-sheikh": { ru: "Доставка цветов в Шарм-эль-Шейхе", en: "Flower delivery in Sharm El Sheikh" },
            hurghada: { ru: "Доставка цветов в Хургаде", en: "Flower delivery in Hurghada" },
            "el-gouna": { ru: "Доставка цветов в Эль-Гуне", en: "Flower delivery in El Gouna" },
            "st-moritz": { ru: "Доставка цветов в Санкт-Морице", en: "Flower delivery in St. Moritz" },
            zermatt: { ru: "Доставка цветов в Церматте", en: "Flower delivery in Zermatt" },
            verbier: { ru: "Доставка цветов в Вербье", en: "Flower delivery in Verbier" },
            verona: { ru: "Доставка цветов в Вероне", en: "Flower delivery in Verona" },
            bologna: { ru: "Доставка цветов в Болонье", en: "Flower delivery in Bologna" },
            genoa: { ru: "Доставка цветов в Генуе", en: "Flower delivery in Genoa" },
            "porto-rotondo": { ru: "Доставка цветов в Порто-Ротондо", en: "Flower delivery in Porto Rotondo" },
            naples: { ru: "Доставка цветов в Неаполе", en: "Flower delivery in Naples" },
            dubai: { ru: "Доставка цветов в Дубае", en: "Flower delivery in Dubai" },
            "abu-dhabi": { ru: "Доставка цветов в Абу-Даби", en: "Flower delivery in Abu Dhabi" },
            sharjah: { ru: "Доставка цветов в Шардже", en: "Flower delivery in Sharjah" },
            limassol: { ru: "Доставка цветов в Лимассоле", en: "Flower delivery in Limassol" },
            lisbon: { ru: "Доставка цветов в Лиссабоне", en: "Flower delivery in Lisbon" },
            algarve: { ru: "Доставка цветов в Алгарве", en: "Flower delivery in Algarve" },
          };
          const clusterKey = Object.keys(CLUSTERS).find((k) => CLUSTERS[k].cities.includes(citySlug));
          if (!clusterKey) return null;
          const cluster = CLUSTERS[clusterKey];
          const rel = cluster.cities.filter((s) => s !== citySlug && LABELS[s]).slice(0, 6);
          if (rel.length === 0) return null;
          return (
            <div className="bg-white rounded-xl p-6 border border-gray-100 mb-8" data-testid="riviera-interlink">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {cluster.title[lang] || cluster.title.ru}
              </h3>
              <div className="flex flex-wrap gap-3">
                {rel.map((s) => (
                  <Link
                    key={s}
                    to={lang === "en" ? `/${s}?lang=en` : `/${s}`}
                    data-testid={`riviera-link-${s}`}
                    className="inline-flex items-center text-pink-600 hover:text-pink-700 font-medium text-sm transition-colors border border-pink-200 rounded-full px-4 py-2 hover:bg-pink-50"
                  >
                    {LABELS[s][lang] || LABELS[s].ru}
                    <span className="ml-1">&rarr;</span>
                  </Link>
                ))}
              </div>
            </div>
          );
        })()}

        {citySlug === "istanbul" && (
          <div className="bg-white rounded-xl p-6 border border-gray-100 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {lang === "en" ? "Sending flowers to a hotel?" : lang === "tr" ? "Otele mi çiçek gönderiyorsunuz?" : "Отправляете цветы в отель?"}
            </h3>
            <Link
              data-testid="istanbul-hotel-link"
              to={lang === "en" ? "/istanbul/hotel-flower-delivery?lang=en" : lang === "tr" ? "/istanbul/hotel-flower-delivery?lang=tr" : "/istanbul/hotel-flower-delivery"}
              className="inline-flex items-center text-pink-600 hover:text-pink-700 font-medium text-sm transition-colors border border-pink-200 rounded-full px-4 py-2 hover:bg-pink-50"
            >
              {lang === "en" ? "Flower delivery to hotels in Istanbul" : lang === "tr" ? "İstanbul otellerine çiçek teslimatı" : "Доставка цветов в отели Стамбула"}
              <span className="ml-1">&rarr;</span>
            </Link>
          </div>
        )}

        {citySlug === "bangkok" && (
          <div className="bg-white rounded-xl p-6 border border-gray-100 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {lang === "en" ? "Sending flowers to a hotel?" : "Отправляете цветы в отель?"}
            </h3>
            <Link
              data-testid="bangkok-hotel-link"
              to={lang === "en" ? "/bangkok/hotel-flower-delivery?lang=en" : "/bangkok/hotel-flower-delivery"}
              className="inline-flex items-center text-pink-600 hover:text-pink-700 font-medium text-sm transition-colors border border-pink-200 rounded-full px-4 py-2 hover:bg-pink-50"
            >
              {lang === "en" ? "Flower delivery to hotels in Bangkok" : "Доставка цветов в отели Бангкока"}
              <span className="ml-1">&rarr;</span>
            </Link>
          </div>
        )}

        {["st-moritz", "zermatt", "verbier"].includes(citySlug) && (
          <div className="bg-white rounded-xl p-6 border border-gray-100 mb-8" data-testid="switzerland-overview-block">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {lang === "en" ? "How flower delivery works across Switzerland" : "Как устроена доставка цветов по всей Швейцарии"}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {lang === "en"
                ? "Concierges and chalets without house numbers, frost and Sundays, francs and euros, and a price comparison of St. Moritz, Zermatt and Verbier — in one overview."
                : "Консьержи и шале без номеров, мороз и воскресенья, франк и евро, сравнение цен Санкт-Морица, Церматта и Вербье — в одном обзоре."}
            </p>
            <Link
              data-testid="switzerland-overview-link"
              to={lang === "en" ? "/blog/dostavka-cvetov-v-shvejcarii?lang=en" : "/blog/dostavka-cvetov-v-shvejcarii"}
              className="inline-flex items-center text-pink-600 hover:text-pink-700 font-medium text-sm transition-colors border border-pink-200 rounded-full px-4 py-2 hover:bg-pink-50"
            >
              {lang === "en" ? "Flower delivery in Switzerland — overview" : "Доставка цветов в Швейцарии — обзор"}
              <span className="ml-1">&rarr;</span>
            </Link>
          </div>
        )}

        {ITALY_CITIES.includes(citySlug) && (
          <div className="bg-white rounded-xl p-6 border border-gray-100 mb-8" data-testid="italy-overview-block">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {lang === "en" ? "How flower delivery works across Italy" : "Как устроена доставка цветов по всей Италии"}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {lang === "en"
                ? "ZTL zones, car-free Venice and Capri, the surname on the intercom, Ferragosto and mimosa on 8 March, plus a real-price table for all 16 Italian cities — in one overview."
                : "Зоны ZTL, Венеция и Капри без машин, фамилия на домофоне, Феррагосто и мимоза на 8 марта, таблица реальных цен по всем 16 городам Италии — в одном обзоре."}
            </p>
            <Link
              data-testid="italy-overview-link"
              to={lang === "en" ? "/blog/dostavka-cvetov-v-italii?lang=en" : "/blog/dostavka-cvetov-v-italii"}
              className="inline-flex items-center text-pink-600 hover:text-pink-700 font-medium text-sm transition-colors border border-pink-200 rounded-full px-4 py-2 hover:bg-pink-50"
            >
              {lang === "en" ? "Flower delivery in Italy — overview" : "Доставка цветов в Италии — обзор"}
              <span className="ml-1">&rarr;</span>
            </Link>
          </div>
        )}

        {citySlug === "barcelona" && (
          <div className="bg-white rounded-xl p-6 border border-gray-100 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {lang === "en" ? "Sending flowers to a hotel?" : "Отправляете цветы в отель?"}
            </h3>
            <Link
              data-testid="barcelona-hotel-link"
              to={lang === "en" ? "/barcelona/hotel-flower-delivery?lang=en" : "/barcelona/hotel-flower-delivery"}
              className="inline-flex items-center text-pink-600 hover:text-pink-700 font-medium text-sm transition-colors border border-pink-200 rounded-full px-4 py-2 hover:bg-pink-50"
            >
              {lang === "en" ? "Flower delivery to hotels in Barcelona" : "Доставка цветов в отели Барселоны"}
              <span className="ml-1">&rarr;</span>
            </Link>
          </div>
        )}

        <div className="text-center">
          <Link
            to="/blog"
            className="inline-flex items-center text-pink-600 hover:text-pink-700 font-medium text-sm transition-colors"
            data-testid="seo-blog-link"
          >
            {content.blogCta}
            <span className="ml-1">&rarr;</span>
          </Link>
        </div>
      </div>
    </section>
  );
}
